import React, {useState, useEffect} from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import {
    TextField,
    Button,
    Grid,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    Box,
    Typography,
    Paper,
    Divider,
    CircularProgress,
    InputAdornment,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Info as InfoIcon, Person as PersonIcon, DirectionsCar as CarIcon,
    FormatListNumbered as NumberIcon, ColorLens as ColorIcon,
    Notes as NotesIcon, Category as CategoryIcon
} from '@mui/icons-material';
import {styled} from '@mui/material/styles';
import TransportService from '../../services/TransportService';
import UserService from '../../services/UserService';
import {Icon0Circle, Icon1Circle, Icon2Circle, Icon3Circle} from "react-bootstrap-icons";
import {toast} from "react-toastify";

const StyledPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[3],
    '& .MuiFormControl-root': {
        marginBottom: theme.spacing(2),
    },
    '& .section-title': {
        margin: theme.spacing(3, 0, 2),
        fontWeight: 600,
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1),
        }
    },
    '& .form-actions': {
        marginTop: theme.spacing(4),
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2)
    }
}));

const TransportForm = ({onSubmit, initialValues, onCancel, isEditing = false, isViewMode = false}) => {
    const [transportTypes, setTransportTypes] = useState([]);
    const [owners, setOwners] = useState([]);
    const [loading, setLoading] = useState({
        types: true,
        owners: true
    });
    const [error, setError] = useState({
        types: null,
        owners: null
    });

    useEffect(() => {
        // Fetch transport types
        const fetchTransportTypes = async () => {
            try {
                const response = await TransportService.getTransportTypes();
                setTransportTypes(response.data);
                setLoading(prev => ({...prev, types: false}));
            } catch (err) {
                setError(prev => ({...prev, types: 'Transport turlarini yuklab bo\'lmadi'}));
                setLoading(prev => ({...prev, types: false}));
            }
        };

        // Fetch owners
        const fetchOwners = async () => {
            try {
                const response = await UserService.getUserList();
                setOwners(response.data);
                setLoading(prev => ({...prev, owners: false}));
            } catch (err) {
                setError(prev => ({...prev, owners: 'Egolarni yuklab bo\'lmadi'}));
                setLoading(prev => ({...prev, owners: false}));
            }
        };

        fetchTransportTypes();
        fetchOwners();
    }, []);

    const validationSchema = Yup.object({
        tur_id: Yup.string().required('Transport turini tanlang'),
        // ega_id: Yup.string().required('Egoni tanlang'),
        // name: Yup.string().required('Nomi kiritilishi shart'),
        davlat_raqam: Yup.string().required('Davlat raqami kiritilishi shart'),
        tex_passport_raqam: Yup.string().required('Tex pasport raqami kiritilishi shart'),
        rang: Yup.string(), //.required('Rangi kiritilishi shart'),
        izoh: Yup.string()
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            // Here you would typically make an API call to create the transport
            // const response = await TransportService.createTransport(values);
            // onSubmit({ ...values, transport_id: response.data.id });

            // For now, we'll simulate a successful response
            // const mockResponse = {data: {id: 'mock-transport-id'}};
            if (!isEditing){
                const response = await TransportService.addTransport(values);
                if (response && response.success === false) {
                    toast.error("Transport qo‘shishda xatolik, ehtimol tex passport yoki davlat raqami mavjud bolishi mumkin");
                }
                if (response?.success === false) {
                    return;
                }
                onSubmit(response.data);
            }else {
                const res = await TransportService.updateTransport(values.transport_id, values);
                if (res && res.success === false) {
                    toast.error("Transport tahrirlashda xatolik, ehtimol tex passport yoki davlat raqami mavjud bolishi mumkin");
                    return
                }
                if (res?.success === false) {
                    return;
                }
                onSubmit(values);
            }
        } catch (error) {
            console.error('Error creating transport:', error);
            // Handle error appropriately
        } finally {
            setSubmitting(false);
        }
    };

    if (loading.types || loading.owners) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <Formik
            initialValues={{
                tur_id: initialValues.tur_id || '',
                ega_id: initialValues.ega_id || '',
                name: initialValues.name || '',
                davlat_raqam: initialValues.davlat_raqam || '',
                tex_passport_raqam: initialValues.tex_passport_raqam || '',
                rang: initialValues.rang || '',
                izoh: initialValues.izoh || ''
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnMount
        >
            {({isSubmitting, values, handleChange, handleBlur, errors, touched, setFieldValue}) => (
                <StyledPaper>
                    <Form id="transport-form">
                        <Typography variant="h6" className="section-title">
                            <CategoryIcon/> Asosiy ma'lumotlar
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={touched.tur_id && Boolean(errors.tur_id)}
                                             variant="outlined" sx={{minWidth: 120}}>
                                    <InputLabel id="transport-type-label">Transport turi *</InputLabel>
                                    <Field
                                        as={Select}
                                        name="tur_id"
                                        labelId="transport-type-label"
                                        label="Transport turi *"
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <CarIcon color="action"/>
                                            </InputAdornment>
                                        }
                                    >
                                        {transportTypes.map((type) => (
                                            <MenuItem key={type.id} value={type.id}>
                                                {type.name}
                                            </MenuItem>
                                        ))}
                                    </Field>
                                    <FormHelperText>
                                        {touched.tur_id && errors.tur_id}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={touched.ega_id && Boolean(errors.ega_id)}
                                             variant="outlined" sx={{minWidth: 120}}>
                                    <InputLabel id="owner-label">Ega</InputLabel>
                                    <Field
                                        as={Select}
                                        name="ega_id"
                                        labelId="owner-label"
                                        label="Ega *"
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <PersonIcon color="action"/>
                                            </InputAdornment>
                                        }
                                    >
                                        {owners.map((owner) => (
                                            <MenuItem key={owner.id} value={owner.id}>
                                                <Box display="flex" flexDirection="column">
                                                    <span>{owner.name}</span>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {owner.phone}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Field>
                                    <FormHelperText>
                                        {touched.ega_id && errors.ega_id}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Typography variant="h6" className="section-title" style={{marginTop: '32px'}}>
                            <InfoIcon/> Transport ma'lumotlari
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Field
                                    as={TextField}
                                    fullWidth
                                    name="name"
                                    label="Nomi"
                                    variant="outlined"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon1Circle color="action"/>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Field
                                    as={TextField}
                                    fullWidth
                                    name="davlat_raqam"
                                    label="Davlat raqami *"
                                    variant="outlined"
                                    error={touched.davlat_raqam && Boolean(errors.davlat_raqam)}
                                    helperText={touched.davlat_raqam && errors.davlat_raqam}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon2Circle color="action"/>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Field
                                    as={TextField}
                                    fullWidth
                                    name="tex_passport_raqam"
                                    label="Tex pasport raqami *"
                                    variant="outlined"
                                    error={touched.tex_passport_raqam && Boolean(errors.tex_passport_raqam)}
                                    helperText={touched.tex_passport_raqam && errors.tex_passport_raqam}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon3Circle color="action"/>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Field
                                    as={TextField}
                                    fullWidth
                                    name="rang"
                                    label="Rangi"
                                    variant="outlined"
                                    error={touched.rang && Boolean(errors.rang)}
                                    helperText={touched.rang && errors.rang}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {/*<NotesIcon color="action" />*/}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {(error.types || error.owners) && (
                            <Box mt={2} p={2} bgcolor="error.light" borderRadius={1}>
                                <Typography variant="body2" color="error.contrastText">
                                    {error.types || error.owners}
                                </Typography>
                            </Box>
                        )}

                        {!isViewMode && (
                            <Box className="form-actions">
                                {onCancel && (
                                    <Button
                                        variant="outlined"
                                        onClick={onCancel}
                                        disabled={isSubmitting}
                                        sx={{mr: 2}}
                                    >
                                        Bekor qilish
                                    </Button>
                                )}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                    startIcon={isSubmitting ? <CircularProgress size={20}/> : null}
                                >
                                    {isSubmitting ? 'Saqlanmoqda...' : 'Saqlash'}
                                </Button>

                            </Box>
                        )}
                    </Form>
                </StyledPaper>
            )}
        </Formik>
    );
};

export default TransportForm;
